import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtCurrency, fmtDate, fmtDateShort } from "@/lib/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StoreFilter, getActiveStore } from "@/components/StoreFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CreditCard,
  Wallet,
  ArrowDownToLine,
  RefreshCw,
  AlertOctagon,
  BookOpen,
  ExternalLink,
  Landmark,
  ReceiptText,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import PayoutBreakdown from "@/components/PayoutBreakdown";

const money = (cents, ccy = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (ccy || "usd").toUpperCase(),
  }).format((Number(cents) || 0) / 100);

const StatusPill = ({ status }) => {
  const s = (status || "").toLowerCase();
  let cls = "bg-muted text-muted-foreground border-border";
  if (["paid", "succeeded", "available"].includes(s))
    cls = "bg-primary/10 text-primary border-primary/30";
  else if (["pending", "in_transit", "processing", "requires_action"].includes(s))
    cls = "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40";
  else if (["failed", "canceled", "lost", "warning_needs_response"].includes(s))
    cls = "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border capitalize ${cls}`}>
      {s.replaceAll("_", " ") || "—"}
    </span>
  );
};

const CardBrand = ({ brand, last4, bank, achLast4 }) => {
  if (bank || achLast4)
    return (
      <span className="text-xs inline-flex items-center gap-1">
        <Landmark className="w-3 h-3 text-muted-foreground" />
        {bank || "ACH"} ••{achLast4}
      </span>
    );
  if (brand || last4)
    return (
      <span className="text-xs inline-flex items-center gap-1">
        <CreditCard className="w-3 h-3 text-muted-foreground" />
        <span className="capitalize">{brand || "card"}</span> ••{last4}
      </span>
    );
  return <span className="text-xs text-muted-foreground">—</span>;
};

// -------------- OVERVIEW --------------
function OverviewTab({ payouts, netDep, account }) {
  const bal = payouts?.balance;
  const available = bal?.available?.reduce((s, b) => s + b.amount, 0) || 0;
  const pending = bal?.pending?.reduce((s, b) => s + b.amount, 0) || 0;
  const instant = bal?.instant_available?.reduce((s, b) => s + b.amount, 0) || 0;
  const ccy = bal?.available?.[0]?.currency || "usd";

  return (
    <div className="space-y-6">
      {/* Balances */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="surface-card p-5" data-testid="stripe-balance-available">
          <div className="label-eyebrow flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Available now
          </div>
          <div className="font-heading text-3xl font-bold mt-2 num">
            {money(available, ccy)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Ready to pay out to your bank
          </div>
        </div>
        <div className="surface-card p-5" data-testid="stripe-balance-pending">
          <div className="label-eyebrow">Pending</div>
          <div className="font-heading text-3xl font-bold mt-2 num">
            {money(pending, ccy)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Charges still settling
          </div>
        </div>
        <div className="surface-card p-5" data-testid="stripe-balance-instant">
          <div className="label-eyebrow">Instant available</div>
          <div className="font-heading text-3xl font-bold mt-2 num">
            {money(instant, ccy)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Payable now via instant payout
          </div>
        </div>
      </div>

      {/* This-month summary */}
      {netDep?.ok && (
        <div className="surface-card p-5" data-testid="stripe-month-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">This month</div>
              <div className="font-heading text-lg font-semibold mt-1">
                Sales &amp; fees
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Sales gross</div>
              <div className="num font-semibold text-lg mt-1">
                {money(netDep.sales_gross, netDep.currency)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Refunds</div>
              <div className="num font-semibold text-lg mt-1 text-[#a44231]">
                −{money(netDep.sales_refunds, netDep.currency)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Processing fees</div>
              <div className="num font-semibold text-lg mt-1 text-[#a44231]">
                −{money(netDep.processing_fees, netDep.currency)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Platform fees</div>
              <div className="num font-semibold text-lg mt-1 text-[#a44231]">
                −{money(netDep.platform_fees, netDep.currency)}
              </div>
            </div>
            <div className="border-l border-border/60 pl-4">
              <div className="text-muted-foreground text-xs">Merchant net</div>
              <div className="num font-bold text-xl mt-1">
                {money(
                  (netDep.sales_net || 0) - (netDep.total_stripe_cost || 0),
                  netDep.currency
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account + external destinations */}
      {account?.ok && (
        <div className="surface-card p-5" data-testid="stripe-account-card">
          <div className="label-eyebrow">Account</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Business</div>
              <div className="font-medium mt-0.5">
                {account.business_name || account.email || account.id}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {account.email}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {(account.country || "").toUpperCase()} ·{" "}
                {(account.default_currency || "").toUpperCase()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Status</div>
              <div className="flex flex-wrap gap-2 mt-1">
                <StatusPill status={account.charges_enabled ? "available" : "failed"} />
                <span className="text-xs text-muted-foreground">
                  Charges {account.charges_enabled ? "enabled" : "disabled"}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                <StatusPill status={account.payouts_enabled ? "available" : "failed"} />
                <span className="text-xs text-muted-foreground">
                  Payouts {account.payouts_enabled ? "enabled" : "disabled"}
                </span>
              </div>
              {account.payout_schedule && (
                <div className="text-xs text-muted-foreground mt-2">
                  Schedule: {account.payout_schedule.interval}
                  {account.payout_schedule.delay_days
                    ? ` · +${account.payout_schedule.delay_days} day delay`
                    : ""}
                </div>
              )}
            </div>
          </div>
          {account.external_accounts?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Payout destinations
              </div>
              <div className="space-y-1">
                {account.external_accounts.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-3 text-sm py-1.5 border-t border-border/60 first:border-t-0"
                  >
                    <Landmark className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">
                      {e.bank_name || e.brand || e.object}
                    </span>
                    <span className="text-muted-foreground">••{e.last4}</span>
                    <span className="text-xs text-muted-foreground uppercase">
                      {e.currency}
                    </span>
                    {e.default_for_currency && (
                      <span className="pill bg-primary/10 text-primary border-primary/30 text-[10px]">
                        Default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Next payouts */}
      {payouts?.upcoming?.length > 0 && (
        <div className="surface-card p-5" data-testid="stripe-upcoming-payouts">
          <div className="label-eyebrow">Upcoming payouts</div>
          <div className="mt-3 divide-y divide-border/60">
            {payouts.upcoming.slice(0, 6).map((p) => (
              <ExpandablePayoutRow key={p.id} payout={p} compact />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpandablePayoutRow({ payout: p, compact }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`payout-row-${p.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full py-2.5 flex items-center justify-between text-sm hover:bg-secondary/30 -mx-2 px-2 rounded transition-colors"
      >
        <div className="flex items-center gap-3">
          <ChevronRight
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
          />
          <ArrowDownToLine className="w-4 h-4 text-muted-foreground" />
          <div className="text-left">
            <div className="font-medium num">{money(p.amount, p.currency)}</div>
            <div className="text-xs text-muted-foreground">
              {compact ? "Arrives " : ""}
              {p.arrival_date
                ? new Date(p.arrival_date * 1000).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : "—"}{" "}
              · {p.method || "standard"}
            </div>
          </div>
        </div>
        <StatusPill status={p.status} />
      </button>
      {open && (
        <div className="pb-2">
          <PayoutBreakdown payoutId={p.id} />
        </div>
      )}
    </div>
  );
}

// -------------- PAYOUTS --------------
function PayoutsTab({ payouts }) {
  const rows = [...(payouts?.upcoming || []), ...(payouts?.recent || []), ...(payouts?.failed || [])];
  rows.sort((a, b) => (b.created || 0) - (a.created || 0));
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="surface-card overflow-hidden">
      <table className="w-full text-sm" data-testid="stripe-payouts-table">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="w-8"></th>
            <th className="text-left px-4 py-2.5">Payout</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Method</th>
            <th className="text-left px-4 py-2.5">Arrival</th>
            <th className="text-left px-4 py-2.5">Created</th>
            <th className="text-right px-4 py-2.5">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground italic">
                No payouts yet
              </td>
            </tr>
          )}
          {rows.map((p) => {
            const isOpen = expanded === p.id;
            return (
              <Fragment key={p.id}>
                <tr
                  className="hover:bg-secondary/30 cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : p.id)}
                  data-testid={`payout-table-row-${p.id}`}
                >
                  <td className="w-8 px-2">
                    <ChevronRight
                      className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{p.id}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="px-4 py-2.5 text-xs capitalize">
                    {p.method || "—"}
                    {p.automatic ? " · auto" : ""}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {p.arrival_date
                      ? new Date(p.arrival_date * 1000).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {p.created
                      ? new Date(p.created * 1000).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right num font-semibold">
                    {money(p.amount, p.currency)}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-secondary/10">
                    <td colSpan={7} className="p-0">
                      <PayoutBreakdown payoutId={p.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// -------------- CHARGES --------------
function ChargesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const load = async (startingAfter = null) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (startingAfter) params.starting_after = startingAfter;
      const { data } = await api.get("/stripe/charges", { params });
      const next = data.data || [];
      setRows((prev) => (startingAfter ? [...prev, ...next] : next));
      setHasMore(!!data.has_more);
      setCursor(data.next_cursor);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="surface-card overflow-hidden">
      <table className="w-full text-sm" data-testid="stripe-charges-table">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">Customer</th>
            <th className="text-left px-4 py-2.5">Order</th>
            <th className="text-left px-4 py-2.5">Method</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-right px-4 py-2.5">Amount</th>
            <th className="text-right px-4 py-2.5">Fee</th>
            <th className="text-right px-4 py-2.5">Net</th>
            <th className="text-center px-4 py-2.5">Receipt</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground italic">
                No charges found
              </td>
            </tr>
          )}
          {rows.map((c) => (
            <tr key={c.id} className="hover:bg-secondary/30">
              <td className="px-4 py-2.5 text-xs">
                {c.created_iso ? fmtDateShort(c.created_iso) : "—"}
              </td>
              <td className="px-4 py-2.5">
                <div className="font-medium">{c.customer_name || "—"}</div>
                <div className="text-xs text-muted-foreground">
                  {c.customer_email || ""}
                </div>
              </td>
              <td className="px-4 py-2.5">
                {c.matched_order ? (
                  <Link
                    to={`/orders/${c.matched_order.id}`}
                    className="text-primary hover:underline text-xs font-mono"
                    data-testid={`charge-order-link-${c.id}`}
                  >
                    {c.matched_order.number}
                  </Link>
                ) : (
                  <span className="text-xs text-muted-foreground italic">unmatched</span>
                )}
              </td>
              <td className="px-4 py-2.5">
                <CardBrand
                  brand={c.card_brand}
                  last4={c.card_last4}
                  bank={c.ach_bank_name}
                  achLast4={c.ach_last4}
                />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex flex-col gap-1">
                  <StatusPill status={c.status} />
                  {c.refunded && (
                    <span className="text-[10px] text-[#a44231]">
                      Refunded {money(c.amount_refunded, c.currency)}
                    </span>
                  )}
                  {c.disputed && (
                    <span className="text-[10px] text-[#a44231]">Disputed</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-2.5 text-right num font-semibold">
                {money(c.amount, c.currency)}
              </td>
              <td className="px-4 py-2.5 text-right num text-[#a44231]">
                {c.fee != null ? `−${money(c.fee, c.currency)}` : "—"}
              </td>
              <td className="px-4 py-2.5 text-right num font-medium">
                {c.net != null ? money(c.net, c.currency) : "—"}
              </td>
              <td className="px-4 py-2.5 text-center">
                {c.receipt_url ? (
                  <a
                    href={c.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                  >
                    View <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="p-3 border-t border-border/60 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(cursor)}
            disabled={loading}
            data-testid="charges-load-more"
          >
            {loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

// -------------- REFUNDS --------------
function RefundsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stripe/refunds", { params: { limit: 100 } });
        setRows(data.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <div className="surface-card overflow-hidden">
      <table className="w-full text-sm" data-testid="stripe-refunds-table">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-2.5">Date</th>
            <th className="text-left px-4 py-2.5">Charge</th>
            <th className="text-left px-4 py-2.5">Reason</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-right px-4 py-2.5">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {loading && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground italic">
                No refunds
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-secondary/30">
              <td className="px-4 py-2.5 text-xs">{fmtDateShort(r.created_iso)}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{r.charge || r.payment_intent || "—"}</td>
              <td className="px-4 py-2.5 text-xs capitalize">
                {(r.reason || "—").replaceAll("_", " ")}
              </td>
              <td className="px-4 py-2.5">
                <StatusPill status={r.status} />
              </td>
              <td className="px-4 py-2.5 text-right num font-semibold text-[#a44231]">
                −{money(r.amount, r.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------------- BALANCE LEDGER --------------
const TX_TYPES = [
  { value: "all", label: "All types" },
  { value: "charge", label: "Charges" },
  { value: "payment", label: "Payments" },
  { value: "refund", label: "Refunds" },
  { value: "payout", label: "Payouts" },
  { value: "stripe_fee", label: "Stripe fees" },
  { value: "adjustment", label: "Adjustments" },
];

function LedgerTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const load = async (startingAfter = null, t = type) => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (t && t !== "all") params.type = t;
      if (startingAfter) params.starting_after = startingAfter;
      const { data } = await api.get("/stripe/balance-transactions", { params });
      const next = data.data || [];
      setRows((prev) => (startingAfter ? [...prev, ...next] : next));
      setHasMore(!!data.has_more);
      setCursor(data.next_cursor);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(null, type);
  }, [type]);

  const totals = useMemo(() => {
    let amount = 0,
      fee = 0,
      net = 0,
      ccy = "usd";
    rows.forEach((r) => {
      amount += r.amount || 0;
      fee += r.fee || 0;
      net += r.net || 0;
      ccy = r.currency || ccy;
    });
    return { amount, fee, net, ccy };
  }, [rows]);

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="label-eyebrow">Filter</div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[220px] mt-1" data-testid="ledger-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TX_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          Showing {rows.length}
          {hasMore ? "+" : ""} txns · Σ Amount {money(totals.amount, totals.ccy)} · Fees{" "}
          {money(totals.fee, totals.ccy)} · Net {money(totals.net, totals.ccy)}
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="stripe-ledger-table">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Description</th>
              <th className="text-left px-4 py-2.5">Available on</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-right px-4 py-2.5">Fee</th>
              <th className="text-right px-4 py-2.5">Net</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground italic">
                  No transactions
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-secondary/30">
                <td className="px-4 py-2.5 text-xs">{fmtDateShort(t.created_iso)}</td>
                <td className="px-4 py-2.5 text-xs capitalize">
                  {(t.type || "").replaceAll("_", " ")}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[360px] truncate">
                  {t.description || "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {t.available_on_iso ? fmtDateShort(t.available_on_iso) : "—"}
                </td>
                <td
                  className={`px-4 py-2.5 text-right num ${
                    t.amount < 0 ? "text-[#a44231]" : ""
                  }`}
                >
                  {money(t.amount, t.currency)}
                </td>
                <td className="px-4 py-2.5 text-right num text-[#a44231]">
                  {t.fee ? `−${money(t.fee, t.currency)}` : "—"}
                </td>
                <td
                  className={`px-4 py-2.5 text-right num font-medium ${
                    t.net < 0 ? "text-[#a44231]" : ""
                  }`}
                >
                  {money(t.net, t.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasMore && (
          <div className="p-3 border-t border-border/60 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(cursor, type)}
              disabled={loading}
              data-testid="ledger-load-more"
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------- DISPUTES --------------
function DisputesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stripe/disputes", { params: { limit: 50 } });
        setRows(data.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <div className="surface-card overflow-hidden">
      <table className="w-full text-sm" data-testid="stripe-disputes-table">
        <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-4 py-2.5">Opened</th>
            <th className="text-left px-4 py-2.5">Charge</th>
            <th className="text-left px-4 py-2.5">Reason</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Evidence due</th>
            <th className="text-right px-4 py-2.5">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {loading && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground italic">
                No disputes — nice.
              </td>
            </tr>
          )}
          {rows.map((d) => (
            <tr key={d.id} className="hover:bg-secondary/30">
              <td className="px-4 py-2.5 text-xs">{fmtDate(d.created_iso)}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{d.charge || d.payment_intent || "—"}</td>
              <td className="px-4 py-2.5 text-xs capitalize">
                {(d.reason || "—").replaceAll("_", " ")}
              </td>
              <td className="px-4 py-2.5">
                <StatusPill status={d.status} />
              </td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">
                {d.evidence_due_iso ? fmtDate(d.evidence_due_iso) : "—"}
              </td>
              <td className="px-4 py-2.5 text-right num font-semibold">
                {money(d.amount, d.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------------- PAYMENTS (Orders ↔ Stripe reconciliation) --------------
const PAYMENT_FILTERS = [
  { value: "all", label: "All orders" },
  { value: "unpaid", label: "Unpaid" },
  { value: "pending", label: "On the way (pending)" },
  { value: "partial", label: "Partially paid" },
  { value: "paid", label: "Paid (Stripe)" },
  { value: "paid_credit", label: "Paid in credits" },
  { value: "canceled", label: "Canceled" },
];

function PaymentStatePill({ state }) {
  const cls =
    state === "paid"
      ? "bg-primary/10 text-primary border-primary/30"
      : state === "paid_credit"
      ? "bg-[#3d8c6e]/10 text-[#1f5f47] border-[#3d8c6e]/30"
      : state === "partial"
      ? "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40"
      : state === "pending"
      ? "bg-[#3d7a8c]/10 text-[#1f5060] border-[#3d7a8c]/30"
      : state === "canceled"
      ? "bg-muted text-muted-foreground border-border line-through decoration-muted-foreground/50"
      : "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40";
  const label =
    state === "partial"
      ? "Partial"
      : state === "paid"
      ? "Paid"
      : state === "paid_credit"
      ? "Paid · credits"
      : state === "pending"
      ? "On the way"
      : state === "canceled"
      ? "Canceled"
      : "Unpaid";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

function PaymentsTab() {
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("unpaid");
  const [store, setStore] = useState(getActiveStore());
  const [includeInternal, setIncludeInternal] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter && filter !== "all") params.status = filter;
      if (store && store !== "all") params.store = store;
      if (includeInternal) params.include_internal = true;
      if (q.trim()) params.q = q.trim();
      const { data: d } = await api.get("/payments/orders", { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [filter, store, includeInternal]);

  const resync = async () => {
    setSyncing(true);
    try {
      await api.post("/stripe/sync-charges");
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const totalRevenue =
    (data.summary?.paid?.amount || 0) +
    (data.summary?.paid_credit?.amount || 0) +
    (data.summary?.partial?.amount || 0) +
    (data.summary?.unpaid?.amount || 0);
  const paidPct = totalRevenue > 0 ? Math.round((((data.summary?.paid?.amount || 0) + (data.summary?.paid_credit?.amount || 0)) / totalRevenue) * 100) : 0;

  return (
    <div>
      {data.summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="payments-summary">
          <div className="surface-card p-4">
            <div className="label-eyebrow">Paid (Stripe)</div>
            <div className="font-heading text-2xl font-bold mt-1 num">
              {fmtCurrency(data.summary.paid.amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data.summary.paid.count} orders · {paidPct}% of revenue
            </div>
          </div>
          <div
            className="surface-card p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
            onClick={() => setFilter("paid_credit")}
            data-testid="payments-summary-paid-credit"
          >
            <div className="label-eyebrow text-[#1f5f47]">Paid in credits</div>
            <div className="font-heading text-2xl font-bold mt-1 num text-[#1f5f47]">
              {fmtCurrency(data.summary.paid_credit?.amount || 0)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data.summary.paid_credit?.count || 0} orders · house account
            </div>
          </div>
          <div className="surface-card p-4">
            <div className="label-eyebrow">Partial</div>
            <div className="font-heading text-2xl font-bold mt-1 num text-[#7c5a14]">
              {fmtCurrency(data.summary.partial.amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data.summary.partial.count} orders · balance due
            </div>
          </div>
          <div className="surface-card p-4">
            <div className="label-eyebrow">On the way</div>
            <div className="font-heading text-2xl font-bold mt-1 num text-[#1f5060]">
              {fmtCurrency(data.summary.pending?.amount || 0)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data.summary.pending?.count || 0} orders · ACH clearing / checkout link
            </div>
          </div>
          <div className="surface-card p-4">
            <div className="label-eyebrow">Unpaid</div>
            <div className="font-heading text-2xl font-bold mt-1 num text-[#a44231]">
              {fmtCurrency(data.summary.unpaid.amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data.summary.unpaid.count} orders
            </div>
          </div>
          <div className="surface-card p-4 bg-[#D66853]/5 border-[#D66853]/30">
            <div className="label-eyebrow">Outstanding total</div>
            <div className="font-heading text-2xl font-bold mt-1 num text-[#a44231]">
              {fmtCurrency(data.summary.outstanding_total)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Owed to you right now
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Show</div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px] mt-1" data-testid="payments-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow">Store</div>
          <div className="mt-1">
            <StoreFilter
              value={store}
              onChange={setStore}
              compact
              testid="payments-store-filter"
            />
          </div>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="label-eyebrow">Search</div>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            onBlur={load}
            placeholder="Order #, customer, PO, or pi_…"
            className="mt-1 w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="payments-search"
          />
        </div>
        <label className="flex items-center gap-2 h-10 self-end px-3 rounded-md border border-input bg-transparent cursor-pointer select-none">
          <Switch
            checked={includeInternal}
            onCheckedChange={setIncludeInternal}
            data-testid="payments-include-internal"
          />
          <span className="text-xs">
            Show internal orders
            <span className="text-muted-foreground ml-1">(paid out-of-band)</span>
          </span>
        </label>
        <Button
          variant="outline"
          onClick={resync}
          disabled={syncing}
          data-testid="payments-resync"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          Resync from Stripe
        </Button>
      </div>

      <div className="text-xs text-muted-foreground mb-3 bg-secondary/40 border border-border/60 rounded px-3 py-2">
        <strong>Truth source:</strong> Stripe settlement (succeeded charges, net of refunds).
        {" "}<strong>Paid in credits</strong> = fully covered by house-account credit (no Stripe activity).
        {" "}<strong>On the way</strong> = a pending Stripe charge exists (ACH clearing, or checkout link created)
        — money is coming but not settled. <strong>Unpaid</strong> = no Stripe activity at all
        (typical for <code className="font-mono">internal_account</code> / on-credit orders).
        Click <em>Resync from Stripe</em> to pull the latest charges.
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Order</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">State</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-right px-4 py-2.5">Paid</th>
              <th className="text-right px-4 py-2.5">Balance</th>
              <th className="text-left px-4 py-2.5">Age</th>
              <th className="text-left px-4 py-2.5">Method</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading && data.rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data.rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground italic">
                  No orders match this filter.
                </td>
              </tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-secondary/30">
                <td className="px-4 py-2.5">
                  <Link
                    to={`/orders/${r.id}`}
                    className="font-mono text-xs text-primary hover:underline"
                    data-testid={`payment-order-${r.number}`}
                  >
                    {r.number}
                  </Link>
                  {r.po_number && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      PO {r.po_number}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="font-medium">
                    {r.customer_company || r.customer_name || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.store_name || ""}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <PaymentStatePill state={r.payment_state} />
                  {r.credit_applied > 0.01 && r.payment_state === "partial" && (
                    <div className="text-[10px] text-[#1f5f47] mt-0.5" data-testid={`credit-badge-${r.number}`}>
                      Credits: {fmtCurrency(r.credit_applied)}
                    </div>
                  )}
                  {r.status === "awaiting_inventory_check" && (
                    <div className="text-[10px] text-[#7c5a14] mt-0.5">
                      Inventory check
                    </div>
                  )}
                  {r.status === "pending_payment" && r.payment_state === "unpaid" && (
                    <div className="text-[10px] text-[#1f5060] mt-0.5">
                      Awaiting payment
                    </div>
                  )}
                  {r.payment_state === "pending" && r.eta_date && (
                    <div className="text-[10px] text-[#1f5060] mt-0.5">
                      ETA {new Date(r.eta_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  )}
                  {r.stripe_has_failed &&
                    r.payment_state !== "pending" &&
                    r.status !== "awaiting_inventory_check" && (
                      <div className="text-[10px] text-[#a44231] mt-0.5">Charge failed</div>
                    )}
                </td>
                <td className="px-4 py-2.5 text-right num">{fmtCurrency(r.total)}</td>
                <td className="px-4 py-2.5 text-right num text-primary">
                  {fmtCurrency(r.paid)}
                </td>
                <td
                  className={`px-4 py-2.5 text-right num font-semibold ${
                    r.balance > 0.01 ? "text-[#a44231]" : "text-muted-foreground"
                  }`}
                >
                  {fmtCurrency(r.balance)}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {r.age_days != null ? `${r.age_days}d` : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div className="capitalize">
                    {(r.payment_method || "—").replaceAll("_", " ")}
                  </div>
                  {r.stripe_ref && (
                    <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[140px]">
                      {r.stripe_ref}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------- MAIN --------------
function relativeTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Stripe() {
  const [payouts, setPayouts] = useState(null);
  const [netDep, setNetDep] = useState(null);
  const [account, setAccount] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab");
    else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const [p, n, a] = await Promise.allSettled([
        api.get("/stripe/payouts"),
        api.get("/stripe/net-deposits"),
        api.get("/stripe/account"),
      ]);
      if (p.status === "fulfilled") setPayouts(p.value.data);
      if (n.status === "fulfilled") setNetDep(n.value.data);
      if (a.status === "fulfilled") setAccount(a.value.data);
    } finally {
      setRefreshing(false);
    }
  };
  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/stripe/sync/status");
      setSyncStatus(data);
    } catch (e) {
      /* silent */
    }
  };
  useEffect(() => {
    load();
    loadSyncStatus();
    const id = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="p-6 lg:p-10 animate-fade-in">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Stripe</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Payments &amp; payouts
          </h1>
          <p className="text-muted-foreground mt-1">
            Every dollar in, every fee, every payout to your bank — live from Stripe.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="stripe-sync-status"
            className="text-xs text-muted-foreground text-right leading-tight"
          >
            <div>
              Last synced{" "}
              <span className="font-medium text-foreground">
                {relativeTime(syncStatus?.last_synced_at)}
              </span>
            </div>
            {syncStatus?.auto_sync_running && syncStatus?.next_sync_in_seconds != null && (
              <div>
                Next auto-sync in {Math.max(0, Math.round(syncStatus.next_sync_in_seconds / 60))}m
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => { load(); loadSyncStatus(); }}
            disabled={refreshing}
            data-testid="stripe-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <a
            href="https://dashboard.stripe.com"
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="outline">
              Stripe dashboard <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </a>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="stripe-tab-overview">
            <Wallet className="w-3.5 h-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="stripe-tab-payments">
            <ReceiptText className="w-3.5 h-3.5 mr-1.5" /> Payments
          </TabsTrigger>
          <TabsTrigger value="payouts" data-testid="stripe-tab-payouts">
            <ArrowDownToLine className="w-3.5 h-3.5 mr-1.5" /> Payouts
          </TabsTrigger>
          <TabsTrigger value="charges" data-testid="stripe-tab-charges">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Charges
          </TabsTrigger>
          <TabsTrigger value="refunds" data-testid="stripe-tab-refunds">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refunds
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="stripe-tab-ledger">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Balance ledger
          </TabsTrigger>
          <TabsTrigger value="disputes" data-testid="stripe-tab-disputes">
            <AlertOctagon className="w-3.5 h-3.5 mr-1.5" /> Disputes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab payouts={payouts} netDep={netDep} account={account} />
        </TabsContent>
        <TabsContent value="payments">
          <PaymentsTab />
        </TabsContent>
        <TabsContent value="payouts">
          <PayoutsTab payouts={payouts} />
        </TabsContent>
        <TabsContent value="charges">
          <ChargesTab />
        </TabsContent>
        <TabsContent value="refunds">
          <RefundsTab />
        </TabsContent>
        <TabsContent value="ledger">
          <LedgerTab />
        </TabsContent>
        <TabsContent value="disputes">
          <DisputesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
